import { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useVersion } from '@/contexts/VersionContext';
import { getMetrics } from '@/utils/versionUtils';
import type { Metric, CategorizedMetrics } from '../types/metricTypes';
import { useCompanyData } from '@/hooks/useCompanyData';

/**
 * Updated metrics hook that uses the shared company data source
 * This ensures pricing metrics can access the same data as other components
 */
export const useMetricsDataV2 = (pageSubslug: string = 'main') => {
  // UI state
  const [showCurrentOnly, setShowCurrentOnly] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [actualVersion, setActualVersion] = useState<string | null>(null);
  
  // Version context
  const { selectedVersion } = useVersion();
  const [eventVersion, setEventVersion] = useState<string | null>(null);
  
  // Derived state
  const effectiveGlobalVersion = selectedVersion || eventVersion || (window as any).currentGlobalVersion;

  // Get URL parameters
  const { slug } = useParams<{ slug: string }>();

  // Use the shared company data hook instead of Redux selector
  const { data: companyData, isLoading } = useCompanyData({
    slug: slug || '',
    enabled: !!slug
  });
  
  // Extract company from the shared data hook response
  const company = companyData;

  // Get the current page based on subslug
  const currentPage = useMemo(() => {
    if (!company || !company.pages) return null;

    return pageSubslug && pageSubslug !== 'main'
      ? company.pages.find((p: any) => p.subslug === pageSubslug)
      : company.pages.find((p: any) => !p.subslug || p.subslug === '') || 
        (company.pages.length > 0 ? company.pages[0] : null);
  }, [company, pageSubslug]);

  // Construct the full slug (with subslug if available)
  const fullSlug = useMemo(() => {
    if (!slug) return '';
    
    // Clean the pageSubslug by removing any leading dot
    const cleanSubslug = pageSubslug ? pageSubslug.replace(/^\./, '') : '';
    
    // Use dot notation to connect slug and subslug
    return cleanSubslug && cleanSubslug !== 'main' ? `${slug}.${cleanSubslug}` : slug;
  }, [slug, pageSubslug]);

  // Listen for version change events
  useEffect(() => {
    const handleVersionChange = (event: CustomEvent) => {
      setEventVersion(event.detail.version);
    };

    window.addEventListener('versionChange' as any, handleVersionChange);
    return () => {
      window.removeEventListener('versionChange' as any, handleVersionChange);
    };
  }, []);

  // Get metrics using the versionUtils.getMetrics function and supplement with thresholds from GraphQL
  const metrics = useMemo(() => {
    if (!fullSlug) return [];

    // Get all metrics for the company
    const result = getMetrics(fullSlug);

    // Set the actual version we're using
    setActualVersion(result.version);
    
    // Array to store all metrics including those from GraphQL
    let allMetrics = [];
    
    // Check if we got valid data from getMetrics
    if (result.data.length > 0) {
      allMetrics = [...result.data];
    }
    
    // Extract thresholds and pricing_metrics from GraphQL data
    if (company?.pages && Array.isArray(company.pages)) {
      // Get the relevant pages based on pageSubslug
      const relevantPages = pageSubslug && pageSubslug !== 'main' 
        ? company.pages.filter((p: any) => p.subslug === pageSubslug)
        : company.pages.filter((p: any) => !p.subslug || p.subslug === '');
        
      // If no pages match the subslug, use all pages
      const pagesToUse = relevantPages.length > 0 ? relevantPages : company.pages;
      
      // Track all metrics from GraphQL to add them at once
      let metricsFromGraphQL: any[] = [];
      
      // 1. Extract thresholds from all relevant pages
      const thresholdsFromGraphQL = pagesToUse
        .filter((page: any) => page.thresholds && Array.isArray(page.thresholds))
        .flatMap((page: any) => {
          return page.thresholds.map((threshold: any) => ({
            id: threshold.id,
            display_name: threshold.display_name,
            name: threshold.display_name, // For compatibility
            modality: 'Threshold', // Set all these to Threshold modality
            source: 'graphql',
            from_page: page.subslug || 'main',
            quarter: result.version ? result.version.split('/').pop() : null // Set quarter to current version quarter
          }));
        });
      
      if (thresholdsFromGraphQL.length > 0) {
        metricsFromGraphQL = [...metricsFromGraphQL, ...thresholdsFromGraphQL];
      }
      
      // 2. Extract pricing_metrics from all relevant pages
      const pricingMetricsFromGraphQL = pagesToUse
        .filter((page: any) => page.pricing_metrics && Array.isArray(page.pricing_metrics))
        .flatMap((page: any) => {
          return page.pricing_metrics.map((metric: any) => ({
            id: metric.id,
            display_name: metric.display_name,
            name: metric.display_name, // For compatibility
            modality: metric.modality || 'Unknown',
            source: 'graphql',
            from_page: page.subslug || 'main',
            quarter: result.version ? result.version.split('/').pop() : null // Set quarter to current version quarter
          }));
        });
      
      if (pricingMetricsFromGraphQL.length > 0) {
        metricsFromGraphQL = [...metricsFromGraphQL, ...pricingMetricsFromGraphQL];
      }
      
      // 3. Add all metrics from plans (associate metrics with plans)
      const metricsFromPlans = pagesToUse
        .filter((page: any) => page.plans && Array.isArray(page.plans))
        .flatMap((page: any) => {
          return page.plans.flatMap((plan: any) => {
            // Skip plans without charges
            if (!plan.charges || !Array.isArray(plan.charges)) return [];
            
            // Extract metrics from each charge
            return plan.charges.flatMap((charge: any) => {
              if (!charge.metric || !charge.metric.id) return [];
              
              // Create a metric object from the charge's metric
              const metric = {
                id: charge.metric.id,
                display_name: charge.metric.display_name || 'Unknown Metric',
                name: charge.metric.display_name || 'Unknown Metric',
                modality: charge.metric.modality || charge.modality || 'Unknown',
                source: 'graphql_plan',
                from_page: page.subslug || 'main',
                // Set quarter to plan's version or current version quarter
                quarter: plan.version 
                  ? plan.version.split('/').pop() 
                  : (result.version ? result.version.split('/').pop() : null),
                // Associate this metric with the plan for filtering
                associatedPlans: [{
                  id: plan.id,
                  name: plan.name,
                  version: plan.version
                }]
              };
              
              return metric;
            });
          });
        });
      
      if (metricsFromPlans.length > 0) {
        metricsFromGraphQL = [...metricsFromGraphQL, ...metricsFromPlans];
      }
      
      // Add all GraphQL metrics to our collection
      if (metricsFromGraphQL.length > 0) {
        allMetrics = [...allMetrics, ...metricsFromGraphQL];
      }
    }
    
    // Add the metrics array to the metrics array
    return allMetrics;
  }, [company, fullSlug, pageSubslug]);

  // Function to get metrics for the current version
  const getCurrentVersionMetrics = () => {
    if (showCurrentOnly && actualVersion) {
      // If company data is available, filter metrics based on plan associations
      if (company && company.pages) {
        // Get plans for the current version from the relevant pages
        const currentPagePlans = company.pages
          .filter((page: any) => !pageSubslug || pageSubslug === 'main' || page.slug === pageSubslug)
          .flatMap((page: any) => page.plans || [])
          .filter((plan: any) => plan.version === actualVersion || !plan.version);
        
        // Get the IDs of plans that have metrics
        const currentPlanIds = new Set(
          currentPagePlans
            .filter((plan: any) => plan.id)
            .map((plan: any) => plan.id)
        );
        
        // Only include metrics that are associated with current plans
        return metrics.filter((metric: any) => {
          // Keep metrics that have associations with current plans
          if (metric.associatedPlans && metric.associatedPlans.length > 0) {
            return metric.associatedPlans.some((plan: any) => currentPlanIds.has(plan.id));
          }
          
          // Or if they have the same version/quarter as the current version
          if (metric.quarter) {
            const metricQuarter = metric.quarter;
            const currentQuarter = actualVersion.split('/').pop() || '';
            return metricQuarter === currentQuarter;
          }
          
          // Default to including the metric if we can't determine otherwise
          return true;
        });
      }
      
      // If we don't have company plans data, fall back to quarter matching
      const currentQuarter = actualVersion.split('/').pop() || '';
      return metrics.filter((metric: any) => {
        return metric.quarter === currentQuarter;
      });
    }
    
    // If not showing current only, return all metrics
    return metrics;
  };

  // Transform the raw metrics data into a normalized format for display
  const normalizedMetrics = useMemo(() => {
    // Get the filtered metrics based on current version
    const filteredMetrics = getCurrentVersionMetrics();
    
    // Create a Map to deduplicate metrics based on display name and modality
    const uniqueMetricsMap = new Map<string, any>();
    
    // Process all metrics to identify unique ones
    filteredMetrics.forEach((metric: any) => {
      const displayName = metric.display_name || metric.name || 'Unknown';
      const modality = (metric.modality || 'Unknown').toLowerCase();
      // Create a unique key based on display name and modality
      const uniqueKey = `${displayName}|${modality}`;
      
      // If this is a new unique metric or has more data than a previously seen one, use it
      if (!uniqueMetricsMap.has(uniqueKey) || 
          (metric.associatedPlans?.length > (uniqueMetricsMap.get(uniqueKey).associatedPlans?.length || 0))) {
        uniqueMetricsMap.set(uniqueKey, metric);
      }
    });
    
    // Return the deduplicated metrics 
    return Array.from(uniqueMetricsMap.values());
  }, [metrics, showCurrentOnly, actualVersion, company?.pages, pageSubslug]);

  // Categorize metrics by modality for display 
  const categorizedMetrics: CategorizedMetrics = useMemo(() => {
    // Sort function for metrics
    const sortByName = (a: Metric, b: Metric) => {
      const nameA = a.display_name.toLowerCase();
      const nameB = b.display_name.toLowerCase();
      return nameA.localeCompare(nameB);
    };
    
    // Sort and filter metrics into categories
    const usageLicenseCreditMetrics = normalizedMetrics
      .filter(metric => 
        ['usage', 'license', 'credit'].includes((metric.modality || '').toLowerCase())
      )
      .sort(sortByName);
    
    const thresholdFlatMetrics = normalizedMetrics
      .filter(metric => 
        ['threshold', 'flat'].includes((metric.modality || '').toLowerCase())
      )
      .sort(sortByName);
    
    const otherMetrics = normalizedMetrics
      .filter(metric => 
        !['usage', 'license', 'credit', 'threshold', 'flat'].includes((metric.modality || '').toLowerCase())
      )
      .sort(sortByName);
    
    return {
      usageLicenseCreditMetrics,
      thresholdFlatMetrics,
      otherMetrics
    };
  }, [normalizedMetrics]);

  return {
    company,
    metrics,
    normalizedMetrics,
    categorizedMetrics,
    isRefreshing,
    setIsRefreshing,
    showCurrentOnly,
    setShowCurrentOnly,
    actualVersion,
    currentPage,
    isLoading
  };
};
